import { useState, useEffect, useCallback, useMemo } from "react";
import {
  collection, addDoc, deleteDoc, updateDoc, doc, query, orderBy, where, getDocs, onSnapshot, serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../contexts/AuthContext";
import { useEntries } from "./useEntries";

const MONTHS = ["Enero","Febrero","Marzo","Abril","Mayo","Junio",
                "Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"];

// ── Cómo funciona esto ──
//
// "Disponible para invertir" es el dinero que tu % de ahorro/inversión
// (fund_pct, el mismo que ya usa el Panel para la tarjeta "🏦 Inversión
// (X%)") ha ido apartando automáticamente mes a mes sobre tu balance real,
// menos lo que ya has invertido de verdad. Se calcula SIEMPRE EN VIVO a
// partir de tus entradas (useEntries) — no se guarda ningún "pozo" en la
// base de datos, así que no hace falta ningún proceso mensual: crece solo,
// mes a mes, según van entrando datos reales.
//
// Cuando el usuario aporta dinero a una inversión (botón "Aportar"):
//   - Si el importe CABE dentro de lo ya apartado automáticamente, no se
//     toca el Saldo Disponible ni el Historial — ese dinero ya estaba
//     descontado "en la sombra" por el propio % del Panel.
//   - Si el importe SUPERA lo disponible, se crea además una entrada real
//     de tipo Gasto con categoría "💹 Inversión", que sí resta del Saldo
//     Disponible y aparece en el Historial normal — igual que hacen las
//     Huchas al aportar. Se guarda el importe COMPLETO en esa entrada (no
//     se divide en "parte cubierta + parte extra").
export function useInvestments() {
  const { user, profile } = useAuth();
  const { visibleEntries } = useEntries();
  const [investments, setInvestments] = useState([]);
  const [loading, setLoading] = useState(true);

  const workspaceId = profile?.activeWorkspaceId || profile?.personalWorkspaceId || null;
  const investmentsRef = useCallback(() => {
    if (workspaceId) return collection(db, "workspaces", workspaceId, "investments");
    return collection(db, "users", user.uid, "investments");
  }, [workspaceId, user]);
  const investmentDoc = useCallback((id) => {
    if (workspaceId) return doc(db, "workspaces", workspaceId, "investments", id);
    return doc(db, "users", user.uid, "investments", id);
  }, [workspaceId, user]);
  const entriesRef = useCallback(() => {
    if (workspaceId) return collection(db, "workspaces", workspaceId, "entries");
    return collection(db, "users", user.uid, "entries");
  }, [workspaceId, user]);
  const entryDocRef = useCallback((id) => {
    if (workspaceId) return doc(db, "workspaces", workspaceId, "entries", id);
    return doc(db, "users", user.uid, "entries", id);
  }, [workspaceId, user]);

  useEffect(() => {
    // BUGFIX: igual que en useRecurring.js/useHuchas.js — hay que esperar a
    // que "workspaceId" esté disponible (no solo "user") antes de
    // suscribirse, o el onSnapshot falla en silencio y "loading" se queda
    // en true para siempre.
    if (!user || !workspaceId) {
      setLoading(!!user);
      return;
    }
    const ref = investmentsRef();
    const q = query(ref, orderBy("createdAt", "desc"));
    const unsub = onSnapshot(
      q,
      snap => {
        setInvestments(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        setLoading(false);
      },
      (err) => {
        console.error("Error en la suscripción a investments:", err);
        setLoading(false);
      }
    );
    return unsub;
  }, [user, workspaceId]);

  // % configurado (el mismo que usa el Panel)
  const pct = profile?.config?.fund_pct !== undefined ? profile.config.fund_pct : 10;

  const now = new Date();
  const currentMonth = MONTHS[now.getMonth()];
  const currentYear = now.getFullYear();

  // "Fondo" acumulado por el % automático, calculado en vivo sobre el
  // balance real de cada mes que tenga entradas — más cualquier "fondo
  // extra" que el usuario haya añadido a mano desde Añadir entrada
  // (marcado con isInvestmentFundTopUp). Ese fondo extra se excluye del
  // balance usado para el %, para que no se distorsione el cálculo
  // automático, y se suma aparte, entero.
  const autoPool = useMemo(() => {
    const byMonth = {};
    visibleEntries.forEach(e => {
      if (!e.month || !e.year) return;
      const key = `${e.year}-${e.month}`;
      if (!byMonth[key]) byMonth[key] = { inc: 0, exp: 0, topUp: 0 };
      if (e.isInvestmentFundTopUp) { byMonth[key].topUp += e.amount || 0; return; }
      if (e.type === "income")  byMonth[key].inc += e.amount || 0;
      if (e.type === "expense") byMonth[key].exp += e.amount || 0;
    });
    return Object.values(byMonth).reduce((sum, m) => {
      const bal = m.inc - m.exp;
      const autoPct = bal > 0 ? bal * (pct / 100) : 0;
      return sum + autoPct + m.topUp;
    }, 0);
  }, [visibleEntries, pct]);

  // Solo las aportaciones reales cuentan contra el fondo (las antiguas
  // marcadas "migrated" de una versión anterior de esta función eran solo
  // una foto del propio % automático — ahora se calcula en vivo arriba, así
  // que no deben restar del fondo otra vez)
  const investedTotal = useMemo(() =>
    investments.filter(i => !i.migrated).reduce((s, i) => s + (i.amount || 0), 0)
  , [investments]);

  // Disponible para invertir, en TOTAL (acumulado de todos los meses)
  const availableToInvest = Math.max(0, autoPool - investedTotal);

  // "Restante del fondo de UN mes concreto": la asignación (% del balance
  // + fondo extra añadido a mano) de ese mes, menos lo que ya se ha
  // invertido usando esa asignación. Sirve tanto para "este mes" como
  // para cualquier mes pasado que se esté consultando con el buscador.
  function getMonthPoolRemaining(month, year) {
    let inc = 0, exp = 0, topUp = 0;
    visibleEntries.forEach(e => {
      if (e.month === month && e.year === year) {
        if (e.isInvestmentFundTopUp) { topUp += e.amount || 0; return; }
        if (e.type === "income")  inc += e.amount || 0;
        if (e.type === "expense") exp += e.amount || 0;
      }
    });
    const bal = inc - exp;
    const pool = (bal > 0 ? bal * (pct / 100) : 0) + topUp;
    const invested = investments
      .filter(i => !i.migrated && i.month === month && i.year === year)
      .reduce((s, i) => s + (i.amount || 0), 0);
    return Math.max(0, pool - invested);
  }

  // Igual que arriba, pero sumando TODOS los meses de un año concreto —
  // para cuando el buscador de Inversiones filtra solo por año (sin mes).
  function getYearPoolRemaining(year) {
    const byMonth = {};
    visibleEntries.forEach(e => {
      if (e.year !== year || !e.month) return;
      if (!byMonth[e.month]) byMonth[e.month] = { inc: 0, exp: 0, topUp: 0 };
      if (e.isInvestmentFundTopUp) { byMonth[e.month].topUp += e.amount || 0; return; }
      if (e.type === "income")  byMonth[e.month].inc += e.amount || 0;
      if (e.type === "expense") byMonth[e.month].exp += e.amount || 0;
    });
    const pool = Object.values(byMonth).reduce((sum, m) => {
      const bal = m.inc - m.exp;
      return sum + (bal > 0 ? bal * (pct / 100) : 0) + m.topUp;
    }, 0);
    const invested = investments
      .filter(i => !i.migrated && i.year === year)
      .reduce((s, i) => s + (i.amount || 0), 0);
    return Math.max(0, pool - invested);
  }

  // "Este mes": el mismo cálculo de arriba, aplicado siempre al mes real
  // en curso — al invertir, este número va bajando primero, antes de tirar
  // del resto del fondo acumulado de meses anteriores.
  const thisMonthRemaining = useMemo(
    () => getMonthPoolRemaining(currentMonth, currentYear),
    [visibleEntries, investments, pct, currentMonth, currentYear]
  );

  async function addInvestment({ amount, note, platform, assetAmount, symbol, assetName, quantity, coingeckoId, date, forceReal }) {
    if (!user) return;
    // "date" es una fecha exacta elegida en el propio formulario (input
    // type="date", formato YYYY-MM-DD) — si no se pasa, se usa el momento
    // real de ahora. Esto sustituye al antiguo mecanismo que solo permitía
    // fechar según el mes/año seleccionados en el buscador de arriba.
    const now = new Date();
    const d = date ? new Date(date + "T12:00:00") : now;
    const month = MONTHS[d.getMonth()];
    const year = d.getFullYear();
    const datetime = date
      ? `${String(d.getDate()).padStart(2,"0")}/${String(d.getMonth()+1).padStart(2,"0")}/${year} 12:00`
      : `${String(now.getDate()).padStart(2,"0")}/${String(now.getMonth()+1).padStart(2,"0")}/${year} ${String(now.getHours()).padStart(2,"0")}:${String(now.getMinutes()).padStart(2,"0")}`;

    // ¿Cabe dentro de lo ya apartado automáticamente, o hay que restarlo
    // también del Saldo Disponible real? "forceReal" se usa cuando la
    // aportación viene de Añadir entrada (checkbox "¿Destinar a inversión?")
    // — ahí SIEMPRE debe reflejarse en el Panel/Historial, igual que pasa
    // con las Huchas, sin mirar el fondo automático. El "cubierto por el
    // fondo" solo aplica cuando se aporta desde la propia página de
    // Inversiones (para no descontar dos veces un dinero que el % ya
    // había apartado).
    const coveredByPool = forceReal ? false : (amount <= availableToInvest);

    try {
      let entryId = null;
      if (!coveredByPool) {
        const created = await addDoc(entriesRef(), {
          type: "expense",
          cat: "💹 Inversión",
          desc: note ? `Inversión (${note})` : "Inversión",
          payment: "",
          amount,
          month, year, datetime,
          isInvestmentContribution: true,
          addedBy: user.uid,
          createdAt: serverTimestamp(),
        });
        entryId = created.id;
      }

      // "symbol" (ticker real, ej. SPCX, SOL) + "quantity" (número limpio)
      // son el modelo nuevo, pensado para poder sincronizar con precios de
      // mercado en el futuro. "assetAmount" (texto libre tipo "0,008 ETH")
      // se mantiene solo por compatibilidad con aportaciones antiguas que
      // no tenían estos campos — para las nuevas, se recomienda symbol+quantity.
      await addDoc(investmentsRef(), {
        amount,
        note: (note || "").trim(),
        platform: (platform || "").trim(),
        assetAmount: (assetAmount || "").trim(),
        symbol: (symbol || "").trim().toUpperCase(),
        assetName: (assetName || "").trim(),
        quantity: quantity ? parseFloat(quantity) : null,
        coingeckoId: coingeckoId || null,
        month, year, datetime,
        coveredByPool,
        entryId,
        addedBy: user.uid,
        createdAt: serverTimestamp(),
      });
    } catch (err) {
      console.error("Error en addInvestment:", err);
      throw err; // se re-lanza para que quien llame (Anadir.js / Inversiones.js) pueda mostrar el aviso
    }
  }

  // Retirar dinero de una inversión (ej. vendiste parte de tus cripto):
  // el dinero vuelve a tu Saldo Disponible real (se crea un INGRESO, nunca
  // un gasto — "sin gastar"), y a la vez el "Total invertido" baja, lo que
  // automáticamente hace subir "Disponible para invertir" (son la misma
  // resta, solo que en sentido contrario a aportar).
  async function withdrawInvestment({ amount, note, platform, assetAmount, symbol, assetName, quantity, coingeckoId, date }) {
    if (!user) return;
    if (amount > investedTotal) {
      throw new Error("No puedes retirar más de lo que tienes invertido");
    }
    const now = new Date();
    const d = date ? new Date(date + "T12:00:00") : now;
    const month = MONTHS[d.getMonth()];
    const year = d.getFullYear();
    const datetime = date
      ? `${String(d.getDate()).padStart(2,"0")}/${String(d.getMonth()+1).padStart(2,"0")}/${year} 12:00`
      : `${String(now.getDate()).padStart(2,"0")}/${String(now.getMonth()+1).padStart(2,"0")}/${year} ${String(now.getHours()).padStart(2,"0")}:${String(now.getMinutes()).padStart(2,"0")}`;

    try {
      // El dinero retirado siempre entra como un ingreso real en el
      // historial/Panel — nunca como gasto.
      const created = await addDoc(entriesRef(), {
        type: "income",
        cat: "💹 Inversión",
        desc: note ? `Retiro de inversión (${note})` : "Retiro de inversión",
        payment: "",
        amount,
        month, year, datetime,
        isInvestmentWithdrawal: true,
        addedBy: user.uid,
        createdAt: serverTimestamp(),
      });

      // Se guarda como aportación NEGATIVA en Inversiones, para que el
      // "Total invertido" baje justo esa cantidad. La cantidad del activo
      // (quantity) también se guarda en negativo, para que al sumar todo
      // el histórico de ese símbolo, la cantidad neta poseída baje también.
      await addDoc(investmentsRef(), {
        amount: -amount,
        note: (note || "").trim(),
        platform: (platform || "").trim(),
        assetAmount: (assetAmount || "").trim(),
        symbol: (symbol || "").trim().toUpperCase(),
        assetName: (assetName || "").trim(),
        quantity: quantity ? -Math.abs(parseFloat(quantity)) : null,
        coingeckoId: coingeckoId || null,
        month, year, datetime,
        isWithdrawal: true,
        entryId: created.id,
        addedBy: user.uid,
        createdAt: serverTimestamp(),
      });
    } catch (err) {
      console.error("Error en withdrawInvestment:", err);
      throw err;
    }
  }

  // Edita una aportación/retiro ya guardado. Si tiene una entrada real
  // vinculada (entryId), esa entrada se actualiza también con el mismo
  // importe/nota — para que el Historial y el Panel no se queden
  // desincronizados del cambio.
  async function updateInvestment(inv, { amount, note, platform, assetAmount, symbol, assetName, quantity, coingeckoId }) {
    if (!inv) return;
    const signedAmount = inv.isWithdrawal ? -Math.abs(amount) : Math.abs(amount);
    const signedQuantity = quantity
      ? (inv.isWithdrawal ? -Math.abs(parseFloat(quantity)) : Math.abs(parseFloat(quantity)))
      : null;
    await updateDoc(investmentDoc(inv.id), {
      amount: signedAmount,
      note: (note || "").trim(),
      platform: (platform || "").trim(),
      assetAmount: (assetAmount || "").trim(),
      symbol: (symbol || "").trim().toUpperCase(),
      assetName: (assetName || "").trim(),
      quantity: signedQuantity,
      coingeckoId: coingeckoId || null,
    });
    if (inv.entryId) {
      await updateDoc(entryDocRef(inv.entryId), {
        amount: Math.abs(amount),
        desc: inv.isWithdrawal
          ? (note ? `Retiro de inversión (${note})` : "Retiro de inversión")
          : (note ? `Inversión (${note})` : "Inversión"),
      }).catch(() => {});
    }
  }

  async function deleteInvestment(inv) {
    // Admite tanto el objeto completo (para poder borrar también su
    // entrada real asociada, si la tiene) como, por compatibilidad, solo
    // el id.
    const id = typeof inv === "string" ? inv : inv.id;
    const entryId = typeof inv === "object" && inv ? inv.entryId : null;
    await deleteDoc(investmentDoc(id));
    if (entryId) {
      await deleteDoc(entryDocRef(entryId)).catch(() => {});
    }
  }

  // Camino inverso: cuando se borra una ENTRADA desde el Historial que está
  // vinculada a una aportación/retiro de Inversiones (entryId apunta a
  // ella), hay que borrar también ese registro de Inversiones — si no, el
  // dinero se queda "fantasma" ahí aunque ya no exista el movimiento real
  // que lo originó. Se usa una query por "entryId" en vez de guardar el id
  // al revés en la propia entrada, para no tener que tocar useEntries.js.
  async function deleteInvestmentByEntryId(entryId) {
    if (!entryId || !workspaceId) return;
    try {
      const q = query(investmentsRef(), where("entryId", "==", entryId));
      const snap = await getDocs(q);
      await Promise.all(snap.docs.map(d => deleteDoc(d.ref)));
    } catch (err) {
      console.error("Error borrando la inversión vinculada a la entrada:", err);
    }
  }

  // Total invertido en un mes/año concreto. Los registros "migrated" (el
  // % automático de meses pasados, calculado antes de que este cálculo
  // pasara a hacerse en vivo) NUNCA cuentan aquí — no son una aportación
  // real, así que no deben sumarse como "invertido".
  function getMonthTotal(month, year) {
    return investments
      .filter(i => !i.migrated && i.month === month && i.year === year)
      .reduce((s, i) => s + (i.amount || 0), 0);
  }

  // Historial agrupado por mes, ordenado del más reciente al más antiguo,
  // con la variación (€ y %) respecto al mes anterior — para la comparativa.
  // Igual que arriba, se excluyen los registros "migrated".
  function getMonthlyHistory() {
    const map = {};
    investments.filter(i => !i.migrated).forEach(i => {
      const key = `${i.year}-${i.month}`;
      if (!map[key]) map[key] = { year: i.year, month: i.month, total: 0 };
      map[key].total += i.amount || 0;
    });
    const list = Object.values(map).sort((a, b) => {
      if (a.year !== b.year) return b.year - a.year;
      return MONTHS.indexOf(b.month) - MONTHS.indexOf(a.month);
    });
    return list.map((entry, i) => {
      const prev = list[i + 1]; // el siguiente en la lista es el mes anterior (orden desc)
      const diff = prev ? entry.total - prev.total : null;
      const diffPct = prev && prev.total > 0 ? (diff / prev.total) * 100 : null;
      return { ...entry, diff, diffPct };
    });
  }

  const currentMonthTotal = getMonthTotal(currentMonth, currentYear);

  // Total histórico: la suma de todos los meses que han tenido inversión,
  // acumulados sin límite de tiempo (a diferencia del total del mes en
  // curso, que sí se "reinicia" cada mes).
  const totalInvested = investments.filter(i => !i.migrated).reduce((s, i) => s + (i.amount || 0), 0);

  return {
    investments, loading,
    addInvestment, withdrawInvestment, updateInvestment, deleteInvestment, deleteInvestmentByEntryId,
    getMonthTotal, getMonthlyHistory,
    currentMonth, currentYear, currentMonthTotal,
    totalInvested,
    availableToInvest, autoPool, investedTotal, pct,
    thisMonthRemaining, getMonthPoolRemaining, getYearPoolRemaining,
  };
}
